import { useEffect, useState } from 'react'
import {
  Box,
  Card,
  CardHeader,
  CardBody,
  VStack,
  HStack,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Button,
  Spinner,
  Text,
  Center,
  Avatar,
  Divider,
  Flex,
  useColorModeValue,
  Alert,
  AlertIcon,
  AlertDescription,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  Code,
  Badge,
  Heading,
} from '@chakra-ui/react'
import { Save, User, Shield, ShieldCheck, ShieldOff, Key } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { AdminLayout } from '@/components/layout/AdminLayout'
import { AuthGuard } from '@/components/auth/AuthGuard'
import { PasswordGenerator } from '@/components/PasswordGenerator'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { ProfileService } from '@/lib/services/profileService'
import { TwoFactorService, type TwoFactorStatus } from '@/lib/services/twoFactorService'
import { useThemeColors } from '@/hooks/useThemeColors'

interface MeData {
  id?: number
  user_login?: string
  user_email?: string
  first_name?: string
  last_name?: string
  nickname?: string
  biography?: string
  attachment_file?: string
  user_pass?: string
}

export default function ProfilePage() {
  const navigate = useNavigate()
  const [me, setMe] = useState<MeData | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [newPassword, setNewPassword] = useState('')
  const [twoFactorStatus, setTwoFactorStatus] = useState<TwoFactorStatus | null>(null)
  const [loading2FA, setLoading2FA] = useState(true)
  const [enabling2FA, setEnabling2FA] = useState(false)
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null)
  const { isOpen, onOpen, onClose } = useDisclosure()
  const { showError, showSuccess } = useToastNotification()

  const navigateBackOrDashboard = () => {
    const historyState = window.history.state as { idx?: number } | null
    if (typeof historyState?.idx === 'number' && historyState.idx > 0) {
      navigate(-1)
      return
    }
    navigate('/dashboard')
  }

  useEffect(() => {
    let mounted = true
    ProfileService.getMe()
      .then((res) => {
        const data = (res.data as any) || {}
        if (!mounted) return
        setMe({
          id: data.id,
          user_login: data.user_login || data.name,
          user_email: data.email || data.user_email,
          first_name: data.first_name || '',
          last_name: data.last_name || '',
          nickname: data.nickname || '',
          biography: data.biography || '',
          attachment_file: data.attachment_file || '',
        })
        // Set loading to false after setting me to avoid race condition
        setLoading(false)
      })
      .catch((e) => {
        if (!mounted) return
        showError(e)
        setLoading(false)
      })
    return () => {
      mounted = false
    }
  }, [showError])

  useEffect(() => {
    loadTwoFactorStatus()
  }, [])

  const loadTwoFactorStatus = async () => {
    try {
      setLoading2FA(true)
      const status = await TwoFactorService.getStatus()
      setTwoFactorStatus(status)
    } catch (error: any) {
      console.error('Failed to load 2FA status:', error)
      setTwoFactorStatus({
        isEnabled: false,
        isRequired: false,
        hasBackupCodes: false,
        system_enabled: false,
        system_required: false,
        can_disable: true,
      })
    } finally {
      setLoading2FA(false)
    }
  }

  const handleEnable2FA = async () => {
    try {
      setEnabling2FA(true)
      const result = await TwoFactorService.enable()
      if (result.success) {
        showSuccess('Success', 'Two-factor authentication enabled successfully')
        if (result.backupCodes) {
          setBackupCodes(result.backupCodes)
          onOpen()
        }
        await loadTwoFactorStatus()
      } else {
        showError(result.message || 'Failed to enable 2FA')
      }
    } catch (error: any) {
      showError(error.message || 'Failed to enable 2FA')
    } finally {
      setEnabling2FA(false)
    }
  }

  const handleDisable2FA = async () => {
    if (!confirm('Are you sure you want to disable two-factor authentication? This will make your account less secure.')) {
      return
    }
    try {
      setEnabling2FA(true)
      const result = await TwoFactorService.disable()
      if (result.success) {
        showSuccess('Success', 'Two-factor authentication disabled successfully')
        await loadTwoFactorStatus()
      } else {
        showError(result.message || 'Failed to disable 2FA')
      }
    } catch (error: any) {
      showError(error.message || 'Failed to disable 2FA')
    } finally {
      setEnabling2FA(false)
    }
  }

  const handleGenerateBackupCodes = async () => {
    try {
      setEnabling2FA(true)
      const result = await TwoFactorService.generateBackupCodes()
      if (result.success && result.backupCodes) {
        setBackupCodes(result.backupCodes)
        onOpen()
        await loadTwoFactorStatus()
      } else {
        showError(result.message || 'Failed to generate backup codes')
      }
    } catch (error: any) {
      showError(error.message || 'Failed to generate backup codes')
    } finally {
      setEnabling2FA(false)
    }
  }

  const handleSave = async () => {
    if (!me || !me.id) return
    try {
      setSaving(true)
      const profileData: Record<string, any> = {
        id: me.id,
        user_login: me.user_login,
        user_email: me.user_email,
        first_name: me.first_name,
        last_name: me.last_name,
        nickname: me.nickname,
        biography: me.biography,
        attachment_file: me.attachment_file || '',
      }

      if (newPassword) {
        profileData.user_pass = newPassword
      }

      await ProfileService.updateProfile(profileData)
      showSuccess('Saved', 'Profile updated successfully')
      if (newPassword) {
        setNewPassword('')
      }
      navigateBackOrDashboard()
    } catch (e: any) {
      showError(e)
    } finally {
      setSaving(false)
    }
  }

  const colors = useThemeColors()
  const loadingTextColor = colors.text
  const headingColor = colors.heading
  const textColor = colors.text
  const subTextColor = colors.textSecondary
  const borderColor = colors.border

  if (loading || !me) {
    return (
      <AuthGuard>
        <AdminLayout>
          <Box p={4} h="full" overflow="hidden" display="flex" flexDirection="column">
            <Card h="full" display="flex" flexDirection="column">
              <CardBody>
                <Center h="400px">
                  <HStack spacing={3}>
                    <Spinner size="md" />
                    <Text color={loadingTextColor}>
                      {loading ? 'Loading profile…' : 'Failed to load profile.'}
                    </Text>
                  </HStack>
                </Center>
              </CardBody>
            </Card>
          </Box>
        </AdminLayout>
      </AuthGuard>
    )
  }

  const displayName = me.first_name && me.last_name
    ? `${me.first_name} ${me.last_name}`
    : me.nickname || me.user_login || 'User'

  return (
    <AuthGuard>
      <AdminLayout>
        <Box p={4} h="full" overflow="hidden" display="flex" flexDirection="column">
          <Card h="full" display="flex" flexDirection="column" maxW="900px" mx="auto" w="100%">
            <CardHeader flexShrink={0}>
              <HStack spacing={3} mb={2}>
                <Avatar size="md" name={displayName} bg="blue.500" icon={<User size={20} />} />
                <Box flex={1}>
                  <Heading size="md">{displayName}</Heading>
                  <Text fontSize="sm" color={colors.text} mt={1}>
                    {me.user_email}
                  </Text>
                </Box>
              </HStack>
            </CardHeader>
            <CardBody overflowY="auto" flex="1" minH={0}>
              <VStack spacing={4} align="stretch">
                {/* Personal Information */}
                <VStack spacing={3} align="stretch" pt={2}>
                  <Text fontSize="md" fontWeight="semibold" color={textColor}>
                    Personal Information
                  </Text>

                  <HStack spacing={3}>
                    <FormControl>
                      <FormLabel fontSize="sm" fontWeight="medium" color={textColor} mb={1}>First Name</FormLabel>
                      <Input
                        value={me.first_name || ''}
                        onChange={(e) => setMe({ ...me, first_name: e.target.value })}
                        size="md"
                        borderRadius="md"
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="sm" fontWeight="medium" color={textColor} mb={1}>Last Name</FormLabel>
                      <Input
                        value={me.last_name || ''}
                        onChange={(e) => setMe({ ...me, last_name: e.target.value })}
                        size="md"
                        borderRadius="md"
                      />
                    </FormControl>
                  </HStack>

                  <FormControl>
                    <FormLabel fontSize="sm" fontWeight="medium" color={textColor} mb={1}>Nickname</FormLabel>
                    <Input
                      value={me.nickname || ''}
                      onChange={(e) => setMe({ ...me, nickname: e.target.value })}
                      size="md"
                      borderRadius="md"
                      placeholder="Enter a nickname"
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel fontSize="sm" fontWeight="medium" color={textColor} mb={1}>Biography</FormLabel>
                    <Textarea
                      rows={3}
                      value={me.biography || ''}
                      onChange={(e) => setMe({ ...me, biography: e.target.value })}
                      size="md"
                      borderRadius="md"
                      placeholder="Tell us about yourself..."
                    />
                  </FormControl>
                </VStack>

                <Divider />

                {/* Account Settings */}
                <VStack spacing={3} align="stretch">
                  <Text fontSize="md" fontWeight="semibold" color={textColor}>
                    Account Settings
                  </Text>

                  <FormControl>
                    <FormLabel fontSize="sm" fontWeight="medium" color={textColor} mb={1}>Email Address</FormLabel>
                    <Input
                      type="email"
                      value={me.user_email || ''}
                      onChange={(e) => setMe({ ...me, user_email: e.target.value })}
                      size="md"
                      borderRadius="md"
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel fontSize="sm" fontWeight="medium" color={textColor} mb={1}>Username</FormLabel>
                    <Input
                      value={me.user_login || ''}
                      isDisabled
                      size="md"
                      borderRadius="md"
                      bg={colors.sectionBg}
                      color={subTextColor}
                    />
                  </FormControl>

                </VStack>

                <Divider />

                {/* Password Management */}
                <VStack spacing={3} align="stretch">
                  <Text fontSize="md" fontWeight="semibold" color={textColor}>
                    Password Management
                  </Text>
                  <PasswordGenerator
                    value={newPassword}
                    onChange={setNewPassword}
                    label="New Password"
                  />
                  {newPassword && (
                    <Text fontSize="xs" color={subTextColor} mt={-2}>
                      Leave blank to keep your current password. Note: Password will only be updated when you save your profile.
                    </Text>
                  )}
                </VStack>

                <Divider />

                {/* Two-Factor Authentication */}
                <VStack spacing={3} align="stretch">
                  <Text fontSize="md" fontWeight="semibold" color={textColor}>
                    Two-Factor Authentication
                  </Text>

                  {loading2FA ? (
                    <HStack spacing={2}>
                      <Spinner size="sm" />
                      <Text fontSize="sm" color={subTextColor}>Loading 2FA status...</Text>
                    </HStack>
                  ) : (
                    <>
                      {twoFactorStatus?.system_required && (
                        <Alert status="info" borderRadius="md">
                          <AlertIcon />
                          <AlertDescription fontSize="sm">
                            Two-factor authentication is required system-wide. You must keep it enabled.
                          </AlertDescription>
                        </Alert>
                      )}

                      {twoFactorStatus && !twoFactorStatus.system_enabled && (
                        <Alert status="warning" borderRadius="md">
                          <AlertIcon />
                          <AlertDescription fontSize="sm">
                            Two-factor authentication is not enabled system-wide. Contact your administrator to enable it.
                          </AlertDescription>
                        </Alert>
                      )}

                      <HStack spacing={4} align="center">
                        <Shield size={20} color={twoFactorStatus?.isEnabled ? '#48BB78' : '#A0AEC0'} />
                        <Box flex={1}>
                          <HStack spacing={2} mb={1}>
                            <Text fontSize="sm" fontWeight="medium" color={textColor}>
                              2FA Status
                            </Text>
                            <Badge
                              colorScheme={twoFactorStatus?.isEnabled ? 'green' : 'gray'}
                              fontSize="xs"
                            >
                              {twoFactorStatus?.isEnabled ? 'Enabled' : 'Disabled'}
                            </Badge>
                          </HStack>
                          <Text fontSize="xs" color={subTextColor}>
                            {twoFactorStatus?.isEnabled
                              ? `You have ${twoFactorStatus.hasBackupCodes ? 'backup codes available' : 'no backup codes'}. Last used: ${twoFactorStatus.lastUsedAt ? new Date(twoFactorStatus.lastUsedAt).toLocaleDateString() : 'Never'}`
                              : 'Enable two-factor authentication to add an extra layer of security to your account.'
                            }
                          </Text>
                        </Box>
                      </HStack>

                      <HStack spacing={2} pt={2}>
                        {!twoFactorStatus?.isEnabled ? (
                          <Button
                            leftIcon={<ShieldCheck size={16} />}
                            colorScheme="green"
                            size="sm"
                            onClick={handleEnable2FA}
                            isLoading={enabling2FA}
                            isDisabled={twoFactorStatus ? !twoFactorStatus.system_enabled : false}
                          >
                            Enable 2FA
                          </Button>
                        ) : (
                          <>
                            <Button
                              leftIcon={<ShieldOff size={16} />}
                              colorScheme="red"
                              variant="outline"
                              size="sm"
                              onClick={handleDisable2FA}
                              isLoading={enabling2FA}
                              isDisabled={twoFactorStatus?.system_required || !twoFactorStatus?.can_disable}
                            >
                              Disable 2FA
                            </Button>
                            <Button
                              leftIcon={<Key size={16} />}
                              colorScheme="blue"
                              variant="outline"
                              size="sm"
                              onClick={handleGenerateBackupCodes}
                              isLoading={enabling2FA}
                            >
                              Generate Backup Codes
                            </Button>
                          </>
                        )}
                      </HStack>

                      {!twoFactorStatus && (
                        <Alert status="error" borderRadius="md" mt={2}>
                          <AlertIcon />
                          <AlertDescription fontSize="sm">
                            Failed to load 2FA status. Please refresh the page or contact support.
                          </AlertDescription>
                        </Alert>
                      )}
                    </>
                  )}
                </VStack>

                <Divider />

                {/* Actions */}
                <Flex justify="flex-end" pt={1}>
                  <HStack spacing={3}>
                    <Button
                      variant="outline"
                      onClick={navigateBackOrDashboard}
                      isDisabled={saving}
                      size="md"
                      px={5}
                      borderRadius="md"
                    >
                      Cancel
                    </Button>
                    <Button
                      leftIcon={<Save size={16} />}
                      colorScheme="blue"
                      onClick={handleSave}
                      isLoading={saving}
                      loadingText="Saving…"
                      size="md"
                      px={5}
                      borderRadius="md"
                    >
                      Save Changes
                    </Button>
                  </HStack>
                </Flex>
              </VStack>
            </CardBody>
          </Card>

          {/* Backup Codes Modal */}
          <Modal isOpen={isOpen} onClose={onClose} size="lg">
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>Backup Codes</ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <Alert status="warning" mb={4} borderRadius="md">
                  <AlertIcon />
                  <AlertDescription fontSize="sm">
                    <strong>Important:</strong> Save these backup codes in a safe place. You can use them to access your account if you lose access to your email.
                  </AlertDescription>
                </Alert>
                <VStack spacing={2} align="stretch">
                  {backupCodes?.map((code, index) => (
                    <Code key={index} p={2} fontSize="sm" textAlign="center" borderRadius="md">
                      {code}
                    </Code>
                  ))}
                </VStack>
              </ModalBody>
              <ModalFooter>
                <Button colorScheme="blue" onClick={onClose}>
                  I've Saved These Codes
                </Button>
              </ModalFooter>
            </ModalContent>
          </Modal>
        </Box>
      </AdminLayout>
    </AuthGuard>
  )
}

